import UserProfileService from '@/services/amio/user-profile.service'

export const BUG_REPORT_CATEGORY = 'user_reported'
export const BUG_REPORT_SEVERITIES = ['low', 'medium', 'high']
export const MAX_SCREENSHOT_BYTES = 5 * 1024 * 1024
export const ACCEPTED_SCREENSHOT_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif']
export const ACCEPTED_SCREENSHOT_ACCEPT = ACCEPTED_SCREENSHOT_TYPES.join(',')

export function collectBrowserInfo() {
  return {
    user_agent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight
    }
  }
}

export function collectPosthogContext(posthog) {
  if (!posthog) {
    return {
      session_id: null,
      distinct_id: null,
      session_replay_url: null
    }
  }

  return {
    session_id: callPosthog(posthog, 'get_session_id'),
    distinct_id: callPosthog(posthog, 'get_distinct_id'),
    session_replay_url: callPosthog(posthog, 'get_session_replay_url', {
      withTimestamp: true,
      timestampLookBack: 30
    })
  }
}

export function validateScreenshotFile(file) {
  if (!file) return null
  if (!ACCEPTED_SCREENSHOT_TYPES.includes(file.type)) {
    return 'Screenshot must be a PNG, JPG, WebP or GIF image'
  }
  if (file.size > MAX_SCREENSHOT_BYTES) {
    return 'Screenshot must be 5 MB or smaller'
  }
  return null
}

const SCREENSHOT_EXTENSIONS = {
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'image/png': 'png'
}

export function fileFromClipboard(clipboardData) {
  if (!clipboardData) return null

  const items = clipboardData.items
  if (items) {
    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      if (!item || item.kind !== 'file' || !ACCEPTED_SCREENSHOT_TYPES.includes(item.type)) continue
      const file = typeof item.getAsFile === 'function' ? item.getAsFile() : null
      if (file) return namedScreenshotFile(file)
    }
  }

  const files = clipboardData.files
  if (files) {
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      if (file && ACCEPTED_SCREENSHOT_TYPES.includes(file.type)) return namedScreenshotFile(file)
    }
  }

  return null
}

function namedScreenshotFile(file) {
  const ext = SCREENSHOT_EXTENSIONS[file.type] || 'png'
  if (file.name && file.name !== 'blob' && !/^image\.(png|jpe?g|webp|gif)$/i.test(file.name)) {
    return file
  }
  return new File([file], `screenshot.${ext}`, { type: file.type })
}

export function fileToBase64Screenshot(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : ''
      const commaIndex = result.indexOf(',')
      resolve({
        filename: file.name,
        mime_type: file.type,
        data: commaIndex >= 0 ? result.slice(commaIndex + 1) : result
      })
    }
    reader.onerror = () => reject(new Error('Could not read the screenshot'))
    reader.readAsDataURL(file)
  })
}

export async function buildBugReportPayload({
  description,
  severity,
  screenshotFile,
  url,
  posthog
}) {
  const screenshot = screenshotFile ? await fileToBase64Screenshot(screenshotFile) : null

  return {
    description,
    severity,
    category: BUG_REPORT_CATEGORY,
    url,
    browser: collectBrowserInfo(),
    posthog: collectPosthogContext(posthog),
    screenshot,
    reporter_email: UserProfileService.getEmail() || null,
    organization_id: UserProfileService.getOrganizationId() || null
  }
}

function callPosthog(posthog, methodName, ...args) {
  if (typeof posthog[methodName] !== 'function') return null
  try {
    return posthog[methodName](...args) || null
  } catch (err) {
    return null
  }
}
